import { inject, Injectable } from "@angular/core";
import { distinctUntilKeyChanged, filter, Subscription } from "rxjs";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { ExternalInstitutionsStoreCollection } from "@bitwarden/web-vault/app/services/store/data-plugin/external.institutions.store.collection";
import { ExternalCountriesStoreCollection } from "@bitwarden/web-vault/app/services/store/data-plugin/external.countries.store.collection";

import { ExternalForexRatesStoreCollection } from "@bitwarden/web-vault/app/services/store/data-plugin/external.forex-rates.store.collection";
import { ExternalDataBankStoreCollection } from "@bitwarden/web-vault/app/services/store/data-plugin/external.data-bank.store.collection";
import { ExternalBookTransactionStoreCollection } from "@bitwarden/web-vault/app/services/store/data-plugin/external.bookt-transaction.store.collection";
import { CalculatedStoreAbstraction } from "@bitwarden/web-vault/app/services/store/calculated.store.abstraction";
import { toObservable } from "@angular/core/rxjs-interop";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";

@Injectable({
  providedIn: "root",
})
export class DataPluginStoreService extends CalculatedStoreAbstraction {
  protected log: LogService = inject(LogService);
  protected userStoreService: UserStoreService = inject(UserStoreService);

  theInstitutionList = new ExternalInstitutionsStoreCollection();
  theCountryList = new ExternalCountriesStoreCollection();
  forexRates = new ExternalForexRatesStoreCollection();
  databank = new ExternalDataBankStoreCollection();
  booktTransaction = new ExternalBookTransactionStoreCollection();

  private preference$ = toObservable(this.userStoreService.preferences.preferenceView);

  /** Trigger subscriptions **/
  private subscriptions: Subscription[] = [];

  clearStore(): void {
    this.theInstitutionList.clear();
    this.theCountryList.clear();
    this.forexRates.clear();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.databank.clear();
    this.booktTransaction.clear();
    this.log.info("DataPluginStoreService cleared");
  }

  async initialize(): Promise<void> {
    this.log.info("Initializing DataPluginStoreService");
    this.initializeCollectionListener();
    this.theCountryList.triggerChanges();
    this.forexRates.triggerChanges();
    this.databank.triggerChanges();
    this.booktTransaction.triggerChanges();
    /** theInstitutionList is triggered via store dependencies with Preference.userLocationOptions **/
  }

  initializeCollectionListener() {
    const userLocationChange$ = this.preference$.pipe(
      filter((x) => !!x || !!x?.userLocation),
      distinctUntilKeyChanged("userLocation"),
    );
    this.subscriptions.push(
      userLocationChange$.subscribe((preferences) => {
        this.theInstitutionList.triggerChanges();
      }),
    );
  }
}
